import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const EmployeeGrowthChart = ({ data }) => {
    const svgRef = useRef();

    useEffect(() => {
        if (!data || data.length === 0) return;

        // Dimensions
        const width = 400;
        const height = 200;
        const margin = { top: 20, right: 30, bottom: 30, left: 40 };

        // Clear previous SVG
        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        // Scales
        const x = d3.scalePoint()
            .domain(data.map(d => d.month))
            .range([margin.left, width - margin.right]);

        const y = d3.scaleLinear()
            .domain([Math.max(0, d3.min(data, d => d.count) - 5), d3.max(data, d => d.count) + 5])
            .range([height - margin.bottom, margin.top]);

        // Line Generator
        const line = d3.line()
            .x(d => x(d.month))
            .y(d => y(d.count))
            .curve(d3.curveMonotoneX);

        // Tooltip
        const tooltip = d3.select("body").append("div")
            .attr("class", "d3-tooltip");

        // -- Draw Elements --

        // Add X Axis
        svg.append("g")
            .attr("transform", `translate(0,${height - margin.bottom})`)
            .call(d3.axisBottom(x))
            .attr("color", "#94a3b8")
            .call(g => g.select(".domain").attr("stroke", "#e2e8f0"));

        // Add Y Axis
        svg.append("g")
            .attr("transform", `translate(${margin.left},0)`)
            .call(d3.axisLeft(y).ticks(5))
            .attr("color", "#94a3b8")
            .call(g => g.select(".domain").remove())
            .call(g => g.selectAll(".tick line").attr("stroke", "#f1f5f9"));

        // Add Gradient
        const gradientId = "growth-gradient-" + Math.random().toString(36).substr(2, 9);
        const gradient = svg.append("defs")
            .append("linearGradient")
            .attr("id", gradientId)
            .attr("x1", "0%")
            .attr("y1", "0%")
            .attr("x2", "0%")
            .attr("y2", "100%");

        gradient.append("stop")
            .attr("offset", "0%")
            .attr("stop-color", "#3b82f6")
            .attr("stop-opacity", 0.4);

        gradient.append("stop")
            .attr("offset", "100%")
            .attr("stop-color", "#3b82f6")
            .attr("stop-opacity", 0);

        // Add Area fill
        const area = d3.area()
            .x(d => x(d.month))
            .y0(height - margin.bottom)
            .y1(d => y(d.count))
            .curve(d3.curveMonotoneX);

        svg.append("path")
            .datum(data)
            .attr("fill", `url(#${gradientId})`)
            .attr("d", area)
            .style("opacity", 0)
            .transition()
            .duration(1000)
            .style("opacity", 1);

        // Add Line Path with Animation
        const path = svg.append("path")
            .datum(data)
            .attr("fill", "none")
            .attr("stroke", "#3b82f6")
            .attr("stroke-width", 3)
            .attr("stroke-linecap", "round")
            .attr("d", line);

        const totalLength = path.node().getTotalLength();

        path
            .attr("stroke-dasharray", totalLength + " " + totalLength)
            .attr("stroke-dashoffset", totalLength)
            .transition()
            .duration(1500)
            .ease(d3.easeCubicInOut)
            .attr("stroke-dashoffset", 0);

        // Add Interactive Overlay for better hover
        svg.selectAll(".dot-group")
            .data(data)
            .enter().append("g")
            .attr("class", "dot-group")
            .on("mouseover", function (event, d) {
                d3.select(this).select("circle").attr("r", 6).attr("fill", "#3b82f6");
                tooltip.style("visibility", "visible")
                    .html(`<strong>${d.month}</strong><br/>Employees: ${d.count}`);
            })
            .on("mousemove", function (event) {
                tooltip.style("top", (event.pageY - 10) + "px")
                    .style("left", (event.pageX + 10) + "px");
            })
            .on("mouseout", function () {
                d3.select(this).select("circle").attr("r", 4).attr("fill", "#fff");
                tooltip.style("visibility", "hidden");
            })
            .append("circle")
            .attr("cx", d => x(d.month))
            .attr("cy", d => y(d.count))
            .attr("r", 4)
            .attr("fill", "#fff")
            .attr("stroke", "#3b82f6")
            .attr("stroke-width", 2);

        return () => {
            tooltip.remove();
        };

    }, [data]);

    return <svg ref={svgRef} width="100%" height="250" viewBox="0 0 400 250"></svg>;
};

export default EmployeeGrowthChart;
